import { ReactNode, useEffect, useId, useState } from "react";
import { ListItemText, MenuItem, SvgIcon } from "@mui/material";
import {
	CoreButton,
	CoreFormLabel,
	CoreLabelText,
	CoreLabelTextContent,
	CoreModal,
	CoreModalDescription,
	CoreSelectTextField,
	CoreTextField,
	coreToast,
} from "@verity/components/core";
import i18n from "../../../../../i18n";
import { VideoEvent } from "../../../../utilities/useTimestamps";
import styles from "./CustomEventModal.module.css";
import Flag from "../../../../../images/flag.svg?react";

const FLAG_LEVEL_NAMES = ["none", "minor", "major", "severe"] as const;

type CustomEventOptions = {
	flagLevel: number;
	message: string;
};

function InputRow({
	id,
	labelId,
	label,
	children,
}: {
	id?: string;
	labelId?: string;
	label: ReactNode;
	children: ReactNode;
}) {
	return (
		<tr>
			<th>
				{id ? <CoreLabelText id={labelId} htmlFor={id} header={label} /> : <CoreLabelTextContent header={label} />}
			</th>
			<td>{children}</td>
		</tr>
	);
}

function EventTime({ children }: { children: ReactNode }) {
	return (
		<InputRow label={i18n.t("result.components.customEventModalTSX.time")}>
			<div className={styles.fixedText}>{children}</div>
		</InputRow>
	);
}

function FlagIcon({ level }: { level: number }) {
	return (
		<SvgIcon className={`${styles.flagIcon} ${styles[FLAG_LEVEL_NAMES[level]]}`}>
			<Flag />
		</SvgIcon>
	);
}

function FlagLevelEntry({ level }: { level: number }) {
	return (
		<div className={styles.flagLevelEntry}>
			<FlagIcon level={level} />
			{i18n.t(`result.components.customEventModalTSX.${FLAG_LEVEL_NAMES[level]}`)}
		</div>
	);
}

function FlagLevelSelector({
	flagLevel,
	setFlagLevel,
}: {
	flagLevel: number;
	setFlagLevel: (newFlagLevel: number) => void;
}) {
	const inputId = useId();
	const labelId = useId();

	return (
		<InputRow id={inputId} labelId={labelId} label={i18n.t("result.components.customEventModalTSX.flagLevel")}>
			<CoreSelectTextField<number>
				id={inputId}
				value={flagLevel ?? 0}
				onChange={(e) => setFlagLevel(Number(e.target.value))}
				label={i18n.t("result.components.customEventModalTSX.flagLevel")}
				renderValue={(level) => <FlagLevelEntry level={level} />}
				SelectProps={{ labelId: labelId }}
			>
				{FLAG_LEVEL_NAMES.map((_, level) => (
					<MenuItem className={styles.flagLevelEntry} key={level} value={level}>
						<ListItemText primary={<FlagLevelEntry level={level} />} />
					</MenuItem>
				))}
			</CoreSelectTextField>
		</InputRow>
	);
}

function MessageEditor({ message, setMessage }: { message: string; setMessage: (value: string) => void }) {
	return (
		<CoreFormLabel
			labelPlacement="top"
			header={i18n.t("result.components.customEventModalTSX.message")}
			control={
				<CoreTextField
					multiline
					minRows={3}
					maxRows={3}
					value={message}
					onChange={(e) => setMessage(e.target.value)}
					placeholder={i18n.t("result.components.customEventModalTSX.addMessage")}
				/>
			}
		/>
	);
}

export function CreateCustomEventModal({
	onClose,
	onSave,
	displayedTime,
}: {
	onClose: () => void;
	onSave: (options: CustomEventOptions) => Promise<void>;
	displayedTime: string;
}) {
	const [flagLevel, setFlagLevel] = useState(0);
	const [message, setMessage] = useState("");

	const [saving, setSaving] = useState(false);

	return (
		<CoreModal
			open
			onClose={onClose}
			title={i18n.t("result.components.customEventModalTSX.createEvent")}
			buttons={
				<>
					<CoreButton color="secondary" onClick={onClose}>
						{i18n.t("result.components.customEventModalTSX.cancel")}
					</CoreButton>
					<CoreButton
						onClick={async () => {
							setSaving(true);
							try {
								await onSave({ flagLevel, message });
								coreToast.success(i18n.t("result.components.customEventModalTSX.eventCreated"));
								onClose();
							} catch (e) {
								console.error(e);
								coreToast.error(i18n.t("result.components.customEventModalTSX.createEventFailed"));
							} finally {
								setSaving(false);
							}
						}}
						disabled={!message}
						loading={saving}
					>
						{i18n.t("result.components.customEventModalTSX.create")}
					</CoreButton>
				</>
			}
		>
			<div className={styles.content}>
				<CoreModalDescription>{i18n.t("result.components.customEventModalTSX.createDescription")}</CoreModalDescription>
				<div className={styles.options}>
					<table className={styles.inputTable}>
						<FlagLevelSelector flagLevel={flagLevel} setFlagLevel={setFlagLevel} />
						<EventTime>{displayedTime}</EventTime>
					</table>
					<MessageEditor message={message} setMessage={setMessage} />
				</div>
			</div>
		</CoreModal>
	);
}

export function EditCustomEventModal({
	onClose,
	event,
	onSave,
	formatTime,
}: {
	onClose: () => void;
	event: VideoEvent;
	onSave: (options: CustomEventOptions) => Promise<void>;
	formatTime: (time: number) => string;
}) {
	const [flagLevel, setFlagLevel] = useState(event.flag_level);
	const initialMessage = event.details?.message ?? "";
	const [message, setMessage] = useState(initialMessage);
	useEffect(() => {
		setFlagLevel(event.flag_level);
		setMessage(event.details?.message ?? "");
	}, [event]);

	const [saving, setSaving] = useState(false);

	return (
		<CoreModal
			open
			onClose={onClose}
			title={i18n.t("result.components.customEventModalTSX.editEvent")}
			buttons={
				<>
					<CoreButton color="secondary" onClick={onClose}>
						{i18n.t("result.components.customEventModalTSX.cancel")}
					</CoreButton>
					<CoreButton
						onClick={async () => {
							setSaving(true);
							try {
								await onSave({ flagLevel, message });
								coreToast.success(i18n.t("result.components.customEventModalTSX.eventUpdated"));
								onClose();
							} catch (e) {
								console.error(e);
								coreToast.error(i18n.t("result.components.customEventModalTSX.updateEventFailed"));
							} finally {
								setSaving(false);
							}
						}}
						disabled={!message || (flagLevel === event.flag_level && message === initialMessage)}
						loading={saving}
					>
						{i18n.t("result.components.customEventModalTSX.save")}
					</CoreButton>
				</>
			}
		>
			<div className={styles.content}>
				<CoreModalDescription>{i18n.t("result.components.customEventModalTSX.editDescription")}</CoreModalDescription>
				<div className={styles.options}>
					<table className={styles.inputTable}>
						<FlagLevelSelector flagLevel={flagLevel} setFlagLevel={setFlagLevel} />
						<EventTime>{formatTime(event.time)}</EventTime>
					</table>
					<MessageEditor message={message} setMessage={setMessage} />
				</div>
			</div>
		</CoreModal>
	);
}
